/**
 * MODULE: domain/pages/frontend/utils/slashCommandGroups.ts
 * PURPOSE: Group labels and preview hints for the slash command menu
 * DEPENDENCIES: slashCommands
 * PUBLIC_API: SLASH_COMMAND_GROUP_ORDER, SLASH_COMMAND_GROUP_LABELS, groupSlashCommands, slashCommandPreviewHint
 * SIDE_EFFECTS: none
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: 79 / 150
 * RECENT: 2026-05-24 [2.2.1185] — grouped slash menu sections and hover previews
 */

import type { SlashCommand, SlashCommandId } from './slashCommands';

export const SLASH_COMMAND_GROUP_ORDER = ['basic', 'media', 'links', 'ai'] as const;

export type SlashCommandGroupId = (typeof SLASH_COMMAND_GROUP_ORDER)[number];

export const SLASH_COMMAND_GROUP_LABELS: Record<SlashCommandGroupId, string> = {
  basic: 'Basic blocks',
  media: 'Media',
  links: 'Links & CRM',
  ai: 'Ask AI',
};

export type SlashCommandPreview = {
  title: string;
  body: string;
};

const SLASH_COMMAND_PREVIEWS: Partial<Record<SlashCommandId, SlashCommandPreview>> = {
  quote: {
    title: 'Quote',
    body: 'Design is not just what it looks like — design is how it works.',
  },
  callout: {
    title: 'Callout',
    body: 'Use callouts for tips, warnings, and key takeaways.',
  },
  toggle: {
    title: 'Toggle',
    body: 'Hide supporting detail until your reader expands the section.',
  },
  table_of_contents: {
    title: 'On this page',
    body: 'Jump links to H1–H3 headings update as you write.',
  },
  database_embed: {
    title: 'Database embed',
    body: 'Show a live read-only table from another database page.',
  },
  ai_summarise: {
    title: 'AI summarise',
    body: 'Generate a concise summary from this page with Brazn assistant.',
  },
};

export function groupSlashCommands(commands: SlashCommand[]): Array<{
  group: SlashCommandGroupId;
  label: string;
  commands: SlashCommand[];
}> {
  const buckets = new Map<SlashCommandGroupId, SlashCommand[]>();
  for (const command of commands) {
    const group = command.group as SlashCommandGroupId;
    const list = buckets.get(group) ?? [];
    list.push(command);
    buckets.set(group, list);
  }

  return SLASH_COMMAND_GROUP_ORDER.filter((group) => buckets.has(group)).map((group) => ({
    group,
    label: SLASH_COMMAND_GROUP_LABELS[group],
    commands: buckets.get(group) ?? [],
  }));
}

export function getSlashCommandPreview(commandId: SlashCommandId): SlashCommandPreview | null {
  return SLASH_COMMAND_PREVIEWS[commandId] ?? null;
}

export function flattenGroupedSlashCommands(
  groups: Array<{ commands: SlashCommand[] }>
): SlashCommand[] {
  return groups.flatMap((group) => group.commands);
}
